import { useMemo, useState } from 'react';
import type { ComponentManifest, WorkItemResolution } from '@ayumia/contracts';
import { aggregateTextScope, displayText, formAggregates, hasPagedPresentation, layoutOf, seriesAsTable } from '@ayumia/component-runtime';
import { ApiError } from '../api/client';
import { text } from '../app/i18n';
import { setLeaveGuard } from '../app/leave-guard';
import { Dialog, DialogActions } from '../design-system/Overlay';
import { ActionsMenu, type MenuAction } from '../design-system/primitives';
import { errorView, Loading } from '../states/States';
import { DetailRepresentation } from '../views/DetailRepresentation';
import { browserDraftStore, draftKey } from '../views/line-draft-store';
import { loadFormDraft, saveFormDraft, withServerDraft, type FormDraft, type FormDraftValues } from './form-draft';
import { useLoad } from '../views/useLoad';
import { AppServicesContext, useAppServices } from '../views/ViewContext';
import { ActionContextPanel } from './ActionContextPanel';
import { DecisionCardPanel } from './DecisionCardPanel';
import { ReadinessPanel } from './ReadinessPanel';
import { ledgerSummary, type SummaryItem } from './series-ledger';
import { formatNumber, type Answers } from './form-targets';
import { StepTrail } from './StepTrail';
import { TemplateForm, type TemplateSubmission } from './TemplateForm';
import { MediaRefProvider } from './form-v2/media-links';
import { OFFLINE_CONFLICT_TYPE, OfflineConflictPanel } from './OfflineConflictPanel';
import { usesFormV2 } from './form-v2/presentation';
import './task-page.css';
import { titleAnswerFields } from './work-item-title';

/**
 * Écran de tâche générique (ADR-064 §5) : un Work Item résolu par `GetWorkItem` en une réponse —
 * le formulaire qu'il cite, la vue de contexte qu'il déclare, les options d'une décision. Prendre,
 * compléter, rejeter, décider : rien de plus. L'étape suivante est celle du Work Engine ; aucun
 * enchaînement n'est codé ici, aucun Pack n'a d'écran propre.
 *
 * `actionable` est une présentation : le serveur ré-évalue l'assignation, le rôle et la politique à
 * chaque commande, et un refus s'affiche comme tel.
 */

const REASONS: Readonly<Record<string, string>> = {
  WORK_ITEM_CLOSED: 'Cette tâche est terminée.',
  WORK_ITEM_NOT_ASSIGNEE: 'Cette tâche est assignée à quelqu’un d’autre.',
  WORK_ITEM_CLAIMED_BY_OTHER: 'Cette tâche a été prise par quelqu’un d’autre.',
  WORK_ITEM_NOT_IN_ROLE: 'Cette tâche est réservée à un autre rôle.',
  REQUESTER_CANNOT_APPROVE: 'Vous ne décidez pas de votre propre demande.',
};

/** Statut d'un Work Item affiché : son libellé, jamais le code du serveur. */
const STATUS: Readonly<Record<string, string>> = {
  created: 'Créée',
  ready: 'À prendre',
  assigned: 'Assignée',
  in_progress: 'En cours',
  waiting: 'En attente',
  blocked: 'Bloquée',
  completed: 'Terminée',
  failed: 'En échec',
  rejected: 'Rejetée',
  cancelled: 'Annulée',
  escalated: 'Escaladée',
};

function snake(type: string): string {
  return type.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
}

/** L'enregistrement de la vue de contexte : le sujet s'il est du bon type, sinon `<type>_id` des entrées. */
function contextRecordId(resolution: WorkItemResolution): string | undefined {
  const type = resolution.context_view?.view.body.target_object_type;
  if (type === undefined) return undefined;
  const subject = resolution.work_item.subject;
  if (subject !== undefined && subject.type === type) return String(subject.id);
  const fromInputs = resolution.work_item.input_context[`${snake(type)}_id`];
  return typeof fromInputs === 'string' ? fromInputs : undefined;
}

export function TaskPage({ workItemId }: { workItemId: string }) {
  const services = useAppServices();
  // Brouillon de la fiche (« Enregistrer et sortir ») : celui du serveur fait foi (comme le v1), repris d'ici, d'un
  // autre poste ou de la tablette ; la copie du navigateur, rangée par personne et par tâche, n'est qu'un tampon.
  const local = useMemo(() => browserDraftStore(), []);
  const formDraftKey = draftKey({ tenant: String(services.context['tenant_id'] ?? ''), identity: String(services.context['identity_id'] ?? ''), command: 'CompleteWorkItem', sheet: { work_item_id: workItemId } });
  const [refresh, setRefresh] = useState(0);
  const [loaded, reload] = useLoad<WorkItemResolution>(`task:${workItemId}:${refresh}`, async () => (await services.client.query<WorkItemResolution>('GetWorkItem', { work_item_id: workItemId })).data);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ category: string; message: string; fields: Record<string, string> } | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  /** Fiche en étapes validée : l'écran de succès remplace la tâche (addendum (2) d'ADR-141 du 2026-10-19 ; `layout.success`, ADR-146 §2.8). */
  const [success, setSuccess] = useState<FormSuccess | null>(null);
  const [serverDraft] = useLoad<FormDraft | null>(`task-draft:${workItemId}:${refresh}`, () => loadFormDraft(services.client, workItemId).catch(() => null));
  const draftValue = serverDraft.status === 'ready' ? serverDraft.value : null;
  const drafts = useMemo(() => withServerDraft(local, draftValue), [local, draftValue]);

  const contextServices = useMemo(() => {
    if (loaded.status !== 'ready' || loaded.value.context_view === undefined) return null;
    const view = loaded.value.context_view;
    const components = new Map<string, ComponentManifest>(services.components);
    for (const component of view.components) components.set(component.artifact_id, component.body);
    const componentRun = new Map<string, boolean>(services.componentRun);
    for (const component of view.components) if (component.run_enabled !== undefined) componentRun.set(component.artifact_id, component.run_enabled);
    return { ...services, components, componentRun, commands: new Map((view.commands ?? []).map((entry) => [entry.command_name, entry] as const)) };
  }, [loaded, services]);

  if (success !== null) return <FormSuccessView success={success} onHome={() => services.navigate({ kind: 'inbox' })} />;
  if (loaded.status === 'loading' || serverDraft.status === 'loading') return <Loading />;
  if (loaded.status === 'error') return <>{errorView(loaded.error, reload)}</>;
  const resolution = loaded.value;
  const item = resolution.work_item;
  const approval = item.required_action.approval;
  const subject = item.subject === undefined ? undefined : { type: item.subject.type, id: String(item.subject.id) };
  const target = { type: 'WorkItem', id: workItemId };
  // Conflit de synchronisation : il se décide par « Rejouer la saisie » ou « Écarter », jamais par Terminer.
  const conflict = item.work_item_type === OFFLINE_CONFLICT_TYPE && (item.status === 'ready' || item.status === 'in_progress');

  const send = async (name: string, payload: Record<string, unknown>, done: 'stay' | 'inbox'): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await services.client.command(name, { payload, target });
      if (name === 'CompleteWorkItem') drafts.clear(formDraftKey);
      if (done === 'inbox') {
        // La tâche est traitée : la fiche n'a plus rien à garder.
        setLeaveGuard(null);
        services.navigate({ kind: 'inbox' });
      }
      else setRefresh((value) => value + 1);
    } catch (caught) {
      refused(caught);
    } finally {
      setBusy(false);
    }
  };
  function refused(caught: unknown): void {
    if (caught instanceof ApiError) {
      const fields: Record<string, string> = {};
      for (const entry of caught.fieldErrors) fields[entry.field.replace(/^payload\.result\./, '').replace(/^result\./, '')] = entry.message;
      setError({ category: caught.category, message: caught.message, fields });
    } else setError({ category: 'INTERNAL_ERROR', message: String(caught), fields: {} });
  }

  /** « Enregistrer et sortir » : le brouillon part au serveur, à la version reprise ; refusé, la fiche reste ouverte. */
  const saveDraft = async (values: FormDraftValues): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      if ((await saveFormDraft(services.client, workItemId, values, draftValue?.version)) === 'newer_elsewhere') {
        setError({ category: 'CONFLICT', message: 'Une saisie plus récente de cette fiche a été enregistrée depuis un autre appareil : votre saisie est gardée sur ce navigateur, rouvrez la fiche pour choisir.', fields: {} });
        return false;
      }
      // La copie du navigateur reste, à l'identique : elle ne sert qu'à défaut du serveur.
      return true;
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'NETWORK_UNAVAILABLE') return true;
      refused(caught);
      return false;
    } finally {
      setBusy(false);
    }
  };

  /** « Valider la fiche » d'une fiche en étapes : la commande part, puis l'écran de succès (référence renvoyée, résumé de la synthèse). */
  const completeStaged = async (submission: TemplateSubmission): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const reply = await services.client.command<{ result?: Record<string, unknown> } | undefined>('CompleteWorkItem', { payload: { result: submission }, target });
      const reference = reply?.result?.['reference'];
      // Seulement après l'accord du serveur : le titre, le message et les actions déclarés par la couche de mise en page.
      const template = resolution.form!.body;
      const declared = layoutOf(template)?.success;
      const message = declared?.message === undefined ? undefined : displayText(declared.message, { result: reply?.result ?? {}, aggregates: aggregateTextScope(formAggregates(template, submission.answers)) });
      drafts.clear(formDraftKey);
      setSuccess({
        ...(typeof reference === 'string' && reference !== '' ? { reference } : {}),
        summary: successSummary(template, submission.answers),
        ...(declared?.title === undefined ? {} : { title: text(declared.title as Readonly<Record<string, string>>, 'fr', 'Fiche envoyée') }),
        ...(message === undefined || message === '' ? {} : { message }),
        actions: (declared?.actions ?? ['back_to_inbox']).filter((action) => WEB_SUCCESS_ACTIONS.has(action)),
      });
    } catch (caught) {
      refused(caught);
    } finally {
      setBusy(false);
    }
  };
  const complete = (submission: TemplateSubmission): void => void (staged ? completeStaged(submission) : send('CompleteWorkItem', { result: submission }, 'inbox'));
  const reasonText = resolution.actionable.reason === undefined || resolution.actionable.reason === 'WORK_ITEM_NOT_CLAIMED' ? null : (REASONS[resolution.actionable.reason] ?? resolution.actionable.reason);
  const denied = error !== null && (error.category === 'AUTHORIZATION_ERROR' || error.category === 'POLICY_REJECTION');
  const recordId = contextRecordId(resolution);
  // L'opérateur du bandeau : la personne connectée, telle que l'application la connaît déjà (contexte de l'expérience).
  const operatorName = typeof services.context['identity_display_name'] === 'string' && services.context['identity_display_name'] !== '' ? services.context['identity_display_name'] : undefined;
  // Fiche en étapes (inspection-template-body 1.6.0) ou à couche de mise en page (1.8.0) : le titre seul en tête,
  // « Rendre au rôle » et « Rejeter » dans le menu ⋮, le fil du procédé seulement s'il dit plus qu'une étape (même règle que la tablette).
  const staged = resolution.form !== undefined && resolution.actionable.can_complete && hasPagedPresentation(resolution.form.body);
  const canRelease = resolution.actionable.can_complete && item.assignee.kind !== 'user';
  const fillable = resolution.form !== undefined && resolution.actionable.can_complete;
  const more: MenuAction[] = [
    ...(staged
      ? [
          ...(canRelease ? [{ id: 'release', label: 'Rendre au rôle', disabled: busy, onSelect: () => void send('ReleaseWorkItem', {}, 'inbox') }] : []),
          { id: 'reject', label: 'Rejeter', disabled: busy, tone: 'danger' as const, onSelect: () => setRejecting(true) },
        ]
      : []),
  ];

  // Formulaire à la présentation 1.10 (bandeau, bande, liste et détail) : plein écran, sans la coquille de
  // l'application, identique à la tablette (décision de l'utilisateur du 2026-10-22, ADR-146 addendum (26)).
  // Le bandeau du formulaire porte le titre, l'opérateur, « Valider » et « Sortir ».
  const fullscreen = fillable && usesFormV2(resolution.form!.body);
  return (
    <section className={`ay-view ay-view--task${fullscreen ? ' ay-view--task-fullscreen' : ''}`} aria-labelledby="ay-task-title" data-testid={fullscreen ? 'task-fullscreen' : undefined}>
      <header className={`ay-view__header${fullscreen ? ' ay-visually-hidden' : ''}`}>
        <h1 id="ay-task-title">{text(item.computed_title ?? item.title)}</h1>
        {!staged && <span className="ay-badge ay-badge--neutral">{STATUS[item.status] ?? item.status}</span>}
        {!staged && resolution.offline_allowed && <span className="ay-badge ay-badge--neutral">Hors ligne possible</span>}
        {more.length > 0 && !conflict && <ActionsMenu label="Autres actions" actions={more} />}
      </header>
      {!fullscreen && (!staged || (resolution.progress?.steps.length ?? 0) > 1) && <StepTrail progress={resolution.progress} />}
      {!fullscreen && item.instructions !== undefined && <p className="ay-text">{text(item.instructions)}</p>}
      {reasonText !== null && (
        <p className="ay-banner ay-banner--warning" role="status">
          {reasonText}
        </p>
      )}
      {error !== null && (
        <p className={`ay-banner ${denied ? 'ay-banner--warning' : 'ay-banner--danger'}`} role="alert">
          {denied ? `Action refusée : ${error.message}` : error.message}
        </p>
      )}

      {conflict && (
        <OfflineConflictPanel
          context={item.input_context}
          busy={busy}
          onDecide={(decision, justification) => void send('ResolveOfflineConflict', { decision, justification }, 'inbox')}
          onOpenTask={(id) => services.navigate({ kind: 'task', work_item_id: id })}
        />
      )}

      {!staged && !conflict && (
      <div className="ay-actionbar" role="toolbar" aria-label="Tâche">
        {resolution.actionable.can_claim && (
          <button type="button" className="ay-button" disabled={busy} onClick={() => void send('ClaimWorkItem', {}, 'stay')}>
            Prendre
          </button>
        )}
        {resolution.actionable.can_complete && resolution.form === undefined && (
          <button type="button" className="ay-button" disabled={busy} onClick={() => void send('CompleteWorkItem', { result: {} }, 'inbox')}>
            Terminer
          </button>
        )}
        {/* Rendre au rôle (ReleaseWorkItem, addendum d'ADR-064) : une tâche prise, pas une tâche assignée nommément. */}
        {resolution.actionable.can_complete && item.assignee.kind !== 'user' && (
          <button type="button" className="ay-button ay-button--secondary" disabled={busy} onClick={() => void send('ReleaseWorkItem', {}, 'inbox')}>
            Rendre au rôle
          </button>
        )}
        {resolution.actionable.can_complete && (
          <button type="button" className="ay-button ay-button--secondary" disabled={busy} onClick={() => setRejecting(true)}>
            Rejeter
          </button>
        )}
      </div>
      )}

      {resolution.form !== undefined && resolution.actionable.can_complete && draftValue !== null && (
        <p className="ay-banner ay-banner--info" role="status" data-testid="draft-resumed">
          Brouillon repris{draftValue.savedAt === '' ? '' : `, enregistré le ${new Date(draftValue.savedAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}`}.
        </p>
      )}
      {resolution.form !== undefined && resolution.actionable.can_complete && (
        // Images des choix tirées du modèle effectif (work-item-resolution 2.6.0, ADR-146 addendum (17)).
        // Références de médiathèque du gabarit (images d'option, du formulaire, des blocs) résolues par GetFileDownloads (ADR-146 addendum (20)).
        <MediaRefProvider client={services.client} template={resolution.form.body} optionImages={resolution.option_images}>
          <TemplateForm template={resolution.form.body} subject={subject} disabled={busy} errors={error?.fields ?? {}} onSubmit={complete} inputs={resolution.work_item.input_context} workItemId={workItemId} templateRef={{ artifact_id: resolution.form.artifact_id, artifact_version: resolution.form.artifact_version }} {...(resolution.conformity_rules === undefined ? {} : { conformityRules: resolution.conformity_rules })} {...(resolution.civil_timezone === undefined ? {} : { timeZones: { site: resolution.civil_timezone.timezone } })} {...(resolution.layout === undefined ? {} : { layout: resolution.layout.body })} {...(resolution.design_recipe === undefined ? {} : { designRecipe: resolution.design_recipe })} {...(resolution.context === undefined ? {} : { context: resolution.context as Readonly<Record<string, unknown>> })} {...(resolution.context?.task.sheet_date === undefined ? {} : { sheetDate: resolution.context.task.sheet_date })} {...(operatorName === undefined ? {} : { operator: operatorName })} draftStore={drafts} draftKey={formDraftKey} onSaveDraft={saveDraft} onLeave={() => services.navigate({ kind: 'inbox' })} titleFields={titleAnswerFields(resolution.work_item.dynamic_title)} />
        </MediaRefProvider>
      )}

      {approval !== undefined && resolution.actionable.can_decide && (
        <section className="ay-card" aria-labelledby="ay-decision-title">
          <h2 id="ay-decision-title">Décision</h2>
          {approval.proposed_action !== undefined && <p className="ay-text">Proposition : {approval.proposed_action}</p>}
          <div className="ay-field">
            <label className="ay-field__label" htmlFor="decision-comment">
              Commentaire
            </label>
            <textarea id="decision-comment" rows={2} value={comment} onChange={(event) => setComment(event.target.value)} disabled={busy} />
          </div>
          <div className="ay-actionbar" role="toolbar" aria-label="Options de décision">
            {(approval.decision_options ?? []).map((option) => (
              <button
                key={option.option_id}
                type="button"
                className={`ay-button${option.option_id === approval.proposed_action ? '' : ' ay-button--secondary'}`}
                disabled={busy}
                onClick={() => void send('DecideApproval', { option_id: option.option_id, ...(comment.trim() === '' ? {} : { comment: comment.trim() }) }, 'inbox')}
              >
                {text(option.label, 'fr', option.option_id)}
              </button>
            ))}
          </div>
        </section>
      )}

      {subject !== undefined && <DecisionCardPanel subject={subject} />}

      <ActionContextPanel workItemId={workItemId} />

      {subject !== undefined && <ReadinessPanel subject={subject} />}

      {contextServices !== null && resolution.context_view !== undefined && recordId !== undefined && (
        <AppServicesContext.Provider value={contextServices}>
          <section className="ay-card" aria-label="Contexte">
            <DetailRepresentation view={resolution.context_view.view.body} object={resolution.context_view.object} recordId={recordId} onChanged={() => undefined} />
          </section>
        </AppServicesContext.Provider>
      )}

      {rejecting && (
        <Dialog title="Rejeter la tâche" open onClose={() => setRejecting(false)} size="sm">
          <form
            className="ay-form"
            onSubmit={(event) => {
              event.preventDefault();
              setRejecting(false);
              void send('RejectWorkItem', { reason: reason.trim() }, 'inbox');
            }}
          >
            <div className="ay-field">
              <label className="ay-field__label" htmlFor="reject-reason">
                Motif
              </label>
              <textarea id="reject-reason" rows={3} required value={reason} onChange={(event) => setReason(event.target.value)} />
            </div>
            <DialogActions destructive={{ label: 'Rejeter la tâche', type: 'submit', disabled: reason.trim() === '' }} cancel={{ label: 'Annuler', onClick: () => setRejecting(false) }} />
          </form>
        </Dialog>
      )}
    </section>
  );
}

/** Résumé de la synthèse d'une fiche validée : les agrégats des séries en registre, sauf le compte par valeur. */
function successSummary(template: NonNullable<WorkItemResolution['form']>['body'], answers: Answers): SummaryItem[] {
  return template.sections
    .filter((section) => section.series !== undefined && seriesAsTable(template, section))
    .flatMap((section) => ledgerSummary(section, (answers[section.section_id] as Answers[] | undefined) ?? []))
    .filter((item) => item.kind !== 'count_by');
}

/** Actions de l'écran de succès que le web sait faire (`layout.success.actions`) ; les autres sont ignorées. */
const WEB_SUCCESS_ACTIONS: ReadonlySet<string> = new Set(['back_to_inbox']);

interface FormSuccess {
  reference?: string;
  summary: SummaryItem[];
  title?: string;
  message?: string;
  /** Actions déclarées que le web sait faire, dans l'ordre déclaré. */
  actions: string[];
}

/**
 * Écran de succès d'une fiche validée (addendum (2) d'ADR-141 du 2026-10-19, même écran que la tablette ;
 * `layout.success`, ADR-146 §2.8) : le titre (« Fiche envoyée » par défaut), le message déclaré, la référence
 * de l'objet créé quand le serveur la renvoie, le résumé de la synthèse, « Accueil ». Le web n'a pas de
 * catalogue d'actions lançables : pas de « Nouvelle fiche ».
 */
function FormSuccessView({ success, onHome }: { success: FormSuccess; onHome: () => void }) {
  const shown = (item: SummaryItem): string =>
    item.kind === 'count' ? `${item.value} ${item.label}` : item.kind === 'sum' ? `${item.label} · ${formatNumber(item.value)}${item.unit === undefined ? '' : ` ${item.unit}`}` : `${item.label} ${item.value}`;
  return (
    <section className="ay-view ay-success" aria-labelledby="ay-success-title" data-testid="form-success">
      <h1 id="ay-success-title">{success.title ?? 'Fiche envoyée'}</h1>
      {success.message !== undefined && (
        <p className="ay-text" data-testid="form-success-message">
          {success.message}
        </p>
      )}
      {success.reference !== undefined && (
        <p className="ay-success__reference" data-testid="form-success-reference">
          {success.reference}
        </p>
      )}
      {success.summary.length > 0 && (
        <ul className="ay-success__summary" aria-label="Résumé">
          {success.summary.map((item) => (
            <li key={item.kind} data-testid={`form-success-${item.kind}`} className={`ay-badge ay-badge--${item.kind === 'nonconform' && item.value > 0 ? 'danger' : item.kind === 'duplicates' && item.value > 0 ? 'warning' : 'neutral'}`}>
              {shown(item)}
            </li>
          ))}
        </ul>
      )}
      <div className="ay-actionbar">
        {/* Sans action que le web sait faire, « Accueil » reste : l'écran n'est jamais une impasse. */}
        {(success.actions.length === 0 ? ['back_to_inbox'] : success.actions).map((action) => (
          <button key={action} type="button" className="ay-button" onClick={onHome}>
            Accueil
          </button>
        ))}
      </div>
    </section>
  );
}
